import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiCheck, FiX } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";

import AdminLayout from "@/layouts/AdminLayout";
import { extractApiErrorMessage } from "@/api/error-message";
import { useProductsList } from "@/api/features/products/queries";
import { useAffiliatePartner } from "@/api/features/affiliate-partners/queries";
import {
  useApproveAffiliatePartnerMutation,
  useRejectAffiliatePartnerMutation,
  useResendAffiliatePartnerPasswordMutation,
  useResendAffiliatePartnerWelcomeEmailMutation,
  useUpdateAffiliatePartnerMutation,
} from "@/api/features/affiliate-partners/mutations";

import AffiliatePartnerForm, {
  createEmptyAffiliatePartnerForm,
  type AffiliatePartnerFormValues,
} from "./AffiliatePartnerForm";

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((part, idx) => ({
    href: `/${parts.slice(0, idx + 1).join("/")}`,
    label: part
      .split("-")
      .map((segment) => segment[0]?.toUpperCase() + segment.slice(1))
      .join(" "),
  }));

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((crumb, index) => (
          <li key={crumb.href} className="flex items-center gap-1">
            <span>/</span>
            {index < crumbs.length - 1 ? (
              <Link to={crumb.href} className="hover:text-gray-700">
                {crumb.label}
              </Link>
            ) : (
              <span className="text-gray-700">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}


function mapDetailToForm(detail: any): AffiliatePartnerFormValues {
  return {
    companyName: detail.companyName ?? "",
    website: detail.website ?? "",
    gstNumber: detail.gstNumber ?? "",
    contactFirstName: detail.contactFirstName ?? "",
    contactLastName: detail.contactLastName ?? "",
    contactEmail: detail.contactEmail ?? "",
    contactPhone: detail.contactPhone ?? "",
    city: detail.city ?? "",
    state: detail.state ?? "",
    country: detail.country ?? "",
    notes: detail.notes ?? "",
    commissionPercent:
      detail.commissionPercent !== null && detail.commissionPercent !== undefined
        ? String(detail.commissionPercent)
        : "",
    products: Array.isArray(detail.products)
      ? detail.products.map((product: any) => Number(product.productId)).filter(Boolean)
      : [],
  };
}

function mapFormToBody(value: AffiliatePartnerFormValues) {
  return {
    companyName: value.companyName.trim(),
    website: value.website.trim() || null,
    gstNumber: value.gstNumber.trim().toUpperCase() || null,
    contactFirstName: value.contactFirstName.trim(),
    contactLastName: value.contactLastName.trim(),
    contactEmail: value.contactEmail.trim(),
    contactPhone: value.contactPhone.trim() || null,
    city: value.city.trim() || null,
    state: value.state.trim() || null,
    country: value.country.trim() || null,
    notes: value.notes.trim() || null,
    commissionPercent: value.commissionPercent === "" ? null : Number(value.commissionPercent),
    products: value.products,
  };
}

export default function EditAffiliatePartnerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailQuery = useAffiliatePartner(id ? Number(id) : null);
  const productsQuery = useProductsList({ page: 1, limit: 200 });
  const updateMutation = useUpdateAffiliatePartnerMutation();
  const approveMutation = useApproveAffiliatePartnerMutation();
  const rejectMutation = useRejectAffiliatePartnerMutation();
  const resendPasswordMutation = useResendAffiliatePartnerPasswordMutation();
  const resendWelcomeMutation = useResendAffiliatePartnerWelcomeEmailMutation();
  const [formValue, setFormValue] = useState(createEmptyAffiliatePartnerForm());
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    if (detailQuery.data) {
      setFormValue(mapDetailToForm(detailQuery.data));
    }
  }, [detailQuery.data]);

  const combinedError = useMemo(
    () =>
      pageError ||
      extractApiErrorMessage(updateMutation.error) ||
      extractApiErrorMessage(approveMutation.error) ||
      extractApiErrorMessage(rejectMutation.error) ||
      extractApiErrorMessage(resendPasswordMutation.error) ||
      extractApiErrorMessage(resendWelcomeMutation.error),
    [
      approveMutation.error,
      pageError,
      rejectMutation.error,
      resendPasswordMutation.error,
      resendWelcomeMutation.error,
      updateMutation.error,
    ]
  );

  const handleSave = (value: AffiliatePartnerFormValues) => {
    if (!id) return;
    setPageError(null);
    updateMutation.mutate(
      { id, data: mapFormToBody(value) },
      {
        onSuccess: () => navigate("/affiliates"),
      }
    );
  };

  const handleApprove = async () => {
    if (!id) return;
    setPageError(null);

    const commissionPercent = Number(formValue.commissionPercent);
    if (!Number.isFinite(commissionPercent) || commissionPercent < 0 || commissionPercent > 100) {
      setPageError("Enter a commission percentage between 0 and 100 before approval.");
      return;
    }
    if (formValue.products.length === 0) {
      setPageError("Select at least one product before approval.");
      return;
    }

    try {
      await updateMutation.mutateAsync({ id, data: mapFormToBody(formValue) });
      await approveMutation.mutateAsync({
        id,
        data: {
          commissionPercent,
          products: formValue.products,
        },
      });
      navigate("/affiliates");
    } catch {
      // errors surface through mutation state
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setPageError(null);
    try {
      await rejectMutation.mutateAsync(id);
      navigate("/affiliates");
    } catch {
      // errors surface through mutation state
    }
  };

  const handleResendPassword = async () => {
    if (!id) return;
    setPageError(null);
    try {
      await resendPasswordMutation.mutateAsync(id);
    } catch {
      // mutation state handles UI error
    }
  };

  const handleResendWelcome = async () => {
    if (!id) return;
    setPageError(null);
    try {
      await resendWelcomeMutation.mutateAsync(id);
    } catch {
      // mutation state handles UI error
    }
  };

  if (!id) {
    return (
      <AdminLayout>
        <section className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          Missing affiliate id in URL.
        </section>
      </AdminLayout>
    );
  }

  if (detailQuery.isLoading) {
    return (
      <AdminLayout>
        <section className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Loading affiliate partner details…
        </section>
      </AdminLayout>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AdminLayout>
        <section className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
          Affiliate partner could not be loaded. Please go back to the affiliates list and try again.
        </section>
      </AdminLayout>
    );
  }

  const detail = detailQuery.data;
  const busy =
    updateMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    resendPasswordMutation.isPending ||
    resendWelcomeMutation.isPending;

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Edit Affiliate Partner{detail.companyName ? ` — ${detail.companyName}` : ""}
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/affiliates")}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <FiX />
          Back to Affiliates
        </button>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <span>
              Status:{" "}
              <span className="font-semibold text-gray-900">{detail.status}</span>
            </span>
            <span>
              Code:{" "}
              <span className="font-semibold text-gray-900">{detail.affiliateCode || "Pending approval"}</span>
            </span>
          </div>

          {detail.status === "PENDING" ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleReject}
                disabled={busy}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
              >
                {rejectMutation.isPending ? "Rejecting…" : "Reject"}
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <FiCheck />
                {approveMutation.isPending ? "Approving…" : "Approve"}
              </button>
            </div>
          ) : detail.status === "APPROVED" ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleResendWelcome}
                disabled={busy}
                className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60"
              >
                {resendWelcomeMutation.isPending ? "Sending…" : "Resend Welcome Email"}
              </button>
              <button
                type="button"
                onClick={handleResendPassword}
                disabled={busy}
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60"
              >
                {resendPasswordMutation.isPending ? "Sending…" : "Resend Password"}
              </button>
            </div>
          ) : null}
        </div>

        <AffiliatePartnerForm
          title="Product Access"
          submitLabel="Save Changes"
          value={formValue}
          products={productsQuery.data?.items ?? []}
          loadingProducts={productsQuery.isLoading}
          submitting={busy}
          errorMessage={combinedError}
          onChange={setFormValue}
          onSubmit={handleSave}
          onCancel={() => navigate("/affiliates")}
        />
      </section>
    </AdminLayout>
  );
}
